import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { API_URL } from '../utils/Functions';
import TopBar from '../components/TopBar';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ConsultationModal from '../components/ConsultationModal';

const DynamicPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalService, setModalService] = useState('');

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/public/pages/${slug}`)
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success') {
          setPage(data.data);
          document.title = data.data.meta_title || `${data.data.title} | Legal Infos`;
        } else {
          setError(data.message || 'Page not found');
        }
      })
      .catch(err => {
        console.error(err);
        setError('Failed to load page');
      })
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (page && page.content) {
      const container = document.querySelector('.dynamic-page-content-area');
      if (container) {
        const scripts = container.querySelectorAll('script');
        scripts.forEach(oldScript => {
          const newScript = document.createElement('script');
          Array.from(oldScript.attributes).forEach(attr => newScript.setAttribute(attr.name, attr.value));
          newScript.appendChild(document.createTextNode(oldScript.innerHTML));
          oldScript.parentNode.replaceChild(newScript, oldScript);
        });
      }
    }
  }, [page]);

  // Render actual forms in place of ql-form-embed placeholders
  useEffect(() => {
    if (!page || !page.content) return;

    const timer = setTimeout(() => {
      const container = document.querySelector('.dynamic-page-content-area');
      if (!container) return;

      const formEmbeds = container.querySelectorAll('a[href*="#form:"]');
      if (formEmbeds.length > 0) {
        const renderedFormIds = new Set();
        Array.from(formEmbeds).forEach(embed => {
          const href = embed.getAttribute('href');
          const formId = href.split('#form:')[1];
          if (!formId || embed.dataset.rendered) return;

          // Quill editor tends to split a block anchor into multiple separate paragraph anchors when sanitizing.
          // If we already rendered this form ID, remove the stray duplicate fragment to clean up the UI.
          if (renderedFormIds.has(formId)) {
            const parentP = embed.closest('p');
            if (parentP) parentP.remove();
            else embed.remove();
            return;
          }

          renderedFormIds.add(formId);
          embed.dataset.rendered = 'true';

          // Perform the async fetch and render
          (async () => {
            try {
              const res = await fetch(`${API_URL}/api/public/forms/${formId}`);
              const json = await res.json();
              const form = json.data || json;
              const fields = Array.isArray(form.schema) ? form.schema : [];

              const wrapper = document.createElement('div');
              wrapper.style.cssText = 'background:#fff;border-radius:18px;box-shadow:0 8px 32px rgba(0,0,0,0.08);border:1px solid #e8edf4;overflow:hidden;margin:48px auto;max-width:800px;font-family:Inter,sans-serif;';

              // Header
              const header = document.createElement('div');
              header.style.cssText = 'background:linear-gradient(135deg,#061830 0%,#0a2540 100%);padding:28px 32px;display:flex;align-items:center;gap:16px;';
              header.innerHTML = `<div style="width:48px;height:48px;border-radius:14px;background:rgba(207,160,71,0.2);border:1px solid rgba(207,160,71,0.4);display:flex;align-items:center;justify-content:center;color:#CFA047;font-size:22px;flex-shrink:0;"><i class="fa-solid fa-table-list"></i></div><div><div style="font-size:20px;font-weight:800;color:#fff;margin-bottom:4px;">${form.name || 'Contact Form'}</div><div style="font-size:13px;color:rgba(255,255,255,0.6);">${form.description || "Fill in the details below and we'll get back to you shortly."}</div></div>`;

              // Form body
              const body = document.createElement('div');
              body.style.cssText = 'padding:32px;display:grid;grid-template-columns:1fr 1fr;gap:18px;';

              const formEl = document.createElement('form');
              formEl.style.cssText = 'display:contents;';

              const inputStyle = 'width:100%;padding:13px 16px;border:1.5px solid #e0e7ef;border-radius:10px;font-size:14.5px;font-family:Inter,sans-serif;outline:none;background:#f8fafc;color:#1e293b;box-sizing:border-box;transition:border-color 0.2s,box-shadow 0.2s;';

              fields.forEach(field => {
                const group = document.createElement('div');
                const isFullWidth = ['textarea', 'select'].includes(field.type) || fields.length <= 2;
                group.style.cssText = `grid-column:${isFullWidth ? '1/-1' : 'auto'};`;

                const label = document.createElement('label');
                label.textContent = field.label + (field.required ? ' *' : '');
                label.style.cssText = 'display:block;font-size:12px;font-weight:700;color:#5a6a7e;margin-bottom:7px;text-transform:uppercase;letter-spacing:0.4px;';

                let input;
                if (field.type === 'textarea') {
                  input = document.createElement('textarea');
                  input.rows = 4;
                  input.style.cssText = inputStyle + 'resize:vertical;min-height:100px;';
                } else if (field.type === 'select') {
                  input = document.createElement('select');
                  input.style.cssText = inputStyle;
                  const ph = document.createElement('option');
                  ph.value = ''; ph.textContent = '— Select —';
                  input.appendChild(ph);
                  (field.options || []).forEach(opt => {
                    const o = document.createElement('option');
                    o.value = opt; o.textContent = opt;
                    input.appendChild(o);
                  });
                } else {
                  input = document.createElement('input');
                  input.type = field.type || 'text';
                  input.style.cssText = inputStyle;
                }
                input.name = field.name;
                input.placeholder = field.placeholder || field.label;
                if (field.required) input.required = true;
                input.addEventListener('focus', () => { input.style.borderColor = '#CFA047'; input.style.boxShadow = '0 0 0 3px rgba(207,160,71,0.12)'; input.style.background = '#fff'; });
                input.addEventListener('blur', () => { input.style.borderColor = '#e0e7ef'; input.style.boxShadow = 'none'; input.style.background = '#f8fafc'; });

                group.appendChild(label);
                group.appendChild(input);
                formEl.appendChild(group);
              });

              // CAPTCHA (if enabled)
              let refreshCaptcha = null;
              if (form.has_captcha) {
                const captchaWrap = document.createElement('div');
                captchaWrap.style.cssText = 'grid-column:1/-1;margin-top:8px;padding:16px;background:#f8fafc;border:1.5px solid #e0e7ef;border-radius:10px;display:flex;flex-wrap:wrap;align-items:center;gap:16px;';
                
                const captchaLabel = document.createElement('div');
                captchaLabel.innerHTML = `<strong>Security Check:</strong> Type the code shown <span style="color:red">*</span>`;
                captchaLabel.style.cssText = 'font-size:14px;color:#334155;width:100%;margin-bottom:-8px;';
                
                const captchaImgContainer = document.createElement('div');
                captchaImgContainer.style.cssText = 'display:flex;align-items:center;gap:12px;background:#fff;padding:4px 12px;border:1px dashed #cbd5e1;border-radius:8px;';
                
                const captchaImg = document.createElement('img');
                captchaImg.style.cssText = 'height:40px;user-select:none;pointer-events:none;';
                
                const refreshBtn = document.createElement('button');
                refreshBtn.type = 'button';
                refreshBtn.innerHTML = '<i class="fa-solid fa-arrows-rotate"></i>';
                refreshBtn.style.cssText = 'background:none;border:none;color:#64748b;cursor:pointer;font-size:16px;padding:8px;transition:color 0.2s;';
                refreshBtn.addEventListener('mouseenter', () => { refreshBtn.style.color = '#0a2540'; });
                refreshBtn.addEventListener('mouseleave', () => { refreshBtn.style.color = '#64748b'; });
                
                captchaImgContainer.appendChild(captchaImg);
                captchaImgContainer.appendChild(refreshBtn);
                
                const tokenInput = document.createElement('input');
                tokenInput.type = 'hidden';
                tokenInput.name = 'captcha_token';
                
                const codeInput = document.createElement('input');
                codeInput.type = 'text';
                codeInput.name = 'captcha_code';
                codeInput.required = true;
                codeInput.placeholder = 'Type code here';
                codeInput.style.cssText = 'padding:10px 16px;border:1.5px solid #e0e7ef;border-radius:8px;font-size:14.5px;width:150px;outline:none;background:#fff;letter-spacing:1px;';
                codeInput.addEventListener('focus', () => { codeInput.style.borderColor = '#CFA047'; });
                codeInput.addEventListener('blur', () => { codeInput.style.borderColor = '#e0e7ef'; });

                refreshCaptcha = async () => {
                  try {
                    refreshBtn.style.opacity = '0.5';
                    const cRes = await fetch(`${API_URL}/api/public/captcha`);
                    const cData = await cRes.json();
                    if (cData.status === 'success') {
                      captchaImg.src = cData.image_base64;
                      tokenInput.value = cData.captcha_token;
                      codeInput.value = '';
                    }
                  } catch(e) {} finally {
                    refreshBtn.style.opacity = '1';
                  }
                };

                refreshBtn.addEventListener('click', refreshCaptcha);
                await refreshCaptcha();

                captchaWrap.appendChild(captchaLabel);
                captchaWrap.appendChild(captchaImgContainer);
                captchaWrap.appendChild(codeInput);
                captchaWrap.appendChild(tokenInput);
                formEl.appendChild(captchaWrap);
              }

              // Submit button
              const submitWrap = document.createElement('div');
              submitWrap.style.cssText = 'grid-column:1/-1;margin-top:4px;';
              const btn = document.createElement('button');
              btn.type = 'submit';
              btn.innerHTML = 'Submit Request &nbsp;<i class="fa-solid fa-paper-plane"></i>';
              btn.style.cssText = 'background:linear-gradient(135deg,#061830 0%,#0a2540 100%);color:white;border:none;padding:15px 36px;border-radius:12px;font-weight:700;font-size:15px;font-family:Inter,sans-serif;cursor:pointer;box-shadow:0 8px 24px rgba(6,24,48,0.25);transition:all 0.2s;';
              btn.addEventListener('mouseenter', () => { btn.style.transform = 'translateY(-2px)'; btn.style.background = 'linear-gradient(135deg,#CFA047 0%,#b8860b 100%)'; });
              btn.addEventListener('mouseleave', () => { btn.style.transform = ''; btn.style.background = 'linear-gradient(135deg,#061830 0%,#0a2540 100%)'; });
              submitWrap.appendChild(btn);
              formEl.appendChild(submitWrap);

              // Submit handler
              formEl.addEventListener('submit', async (e) => {
                e.preventDefault();

                btn.disabled = true;
                btn.textContent = 'Sending…';
                const data = {};
                new FormData(formEl).forEach((val, key) => { data[key] = val; });
                try {
                  const subRes = await fetch(`${API_URL}/api/public/forms/${formId}/submit`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: JSON.stringify(data)
                  });
                  const subData = await subRes.json();
                  if (!subRes.ok) {
                    if (subData.errors && subData.errors.captcha_code) {
                      alert(subData.errors.captcha_code[0] || 'Incorrect security check code. Please try again.');
                      if (refreshCaptcha) await refreshCaptcha();
                    } else {
                      alert(subData.message || 'Error submitting form.');
                    }
                    btn.disabled = false;
                    btn.innerHTML = 'Submit Request &nbsp;<i class="fa-solid fa-paper-plane"></i>';
                    return;
                  }
                } catch (err) {
                  alert('Network error. Please try again.');
                  btn.disabled = false;
                  btn.innerHTML = 'Submit Request &nbsp;<i class="fa-solid fa-paper-plane"></i>';
                  return;
                }
                body.innerHTML = `<div style="grid-column:1/-1;text-align:center;padding:32px 0;"><div style="width:72px;height:72px;background:linear-gradient(135deg,#ecfdf5,#d1fae5);color:#10b981;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:32px;margin:0 auto 20px;border:3px solid #a7f3d0;"><i class="fa-solid fa-circle-check"></i></div><h4 style="font-size:20px;font-weight:800;color:#0f172a;margin-bottom:8px;">Thank You!</h4><p style="color:#64748b;font-size:14px;">Your submission has been received. We'll get back to you shortly.</p></div>`;
              });

              body.appendChild(formEl);
              wrapper.appendChild(header);
              wrapper.appendChild(body);
              embed.replaceWith(wrapper);
            } catch (err) {
              console.error('Form embed error', err);
            }
          })();
        });
      }

      const packageEmbeds = container.querySelectorAll('a[href*="#package:"]');
      if (packageEmbeds.length > 0) {
        const renderedPackageIds = new Set();
        Array.from(packageEmbeds).forEach(embed => {
          const href = embed.getAttribute('href');
          const packageId = href.split('#package:')[1];
          if (!packageId || embed.dataset.rendered) return;

          if (renderedPackageIds.has(packageId)) {
            const parentP = embed.closest('p');
            if (parentP) parentP.remove();
            else embed.remove();
            return;
          }

          renderedPackageIds.add(packageId);
          embed.dataset.rendered = 'true';

          (async () => {
            try {
              const res = await fetch(`${API_URL}/api/public/packages`);
              const json = await res.json();
              const packages = json.data || [];
              const pkgGroup = packages.find(p => String(p.id) === String(packageId));
              if (!pkgGroup) return;
              
              const wrapper = document.createElement('div');
              wrapper.style.cssText = 'margin:64px 0;font-family:Inter,sans-serif;max-width:1200px;margin-left:auto;margin-right:auto;';

              const header = document.createElement('div');
              header.style.cssText = 'text-align:left;margin-bottom:48px;';
              
              const superTitleHtml = `<div style="display:flex;align-items:center;gap:12px;margin-bottom:16px;"><div style="height:2px;width:32px;background:#CFA047;"></div><span style="color:#CFA047;font-size:13px;font-weight:700;letter-spacing:2px;text-transform:uppercase;">PRICING PLANS</span></div>`;
              
              header.innerHTML = `${superTitleHtml}<h3 style="font-size:36px;font-weight:800;color:#0a192f;margin:0 0 16px;">${pkgGroup.name}</h3>${pkgGroup.description ? `<p style="font-size:16px;color:#64748b;margin:0;line-height:1.6;">${pkgGroup.description}</p>` : ''}`;
              
              wrapper.appendChild(header);

              const grid = document.createElement('div');
              grid.style.cssText = 'display:flex;flex-wrap:wrap;gap:24px;align-items:stretch;justify-content:flex-start;';
              
              const renderPackageCard = (p, isSub = false) => {
                const card = document.createElement('div');
                
                const isFeatured = p.is_featured;
                
                card.style.cssText = `border-radius:16px;padding:40px 32px;display:flex;flex-direction:column;position:relative;flex:1 1 300px;max-width:340px;${isFeatured ? 'background:#0b192c;color:#fff;box-shadow:0 24px 48px rgba(11,25,44,0.15);margin-top:-20px;padding-bottom:50px;' : 'background:#fff;border:1px solid #e2e8f0;color:#0f172a;'}`;
                
                if (isFeatured) {
                  card.innerHTML += `<div style="position:absolute;top:-12px;right:32px;background:#CFA047;color:#000;font-size:11px;font-weight:700;padding:6px 16px;border-radius:20px;letter-spacing:1px;text-transform:uppercase;box-shadow:0 4px 12px rgba(207,160,71,0.3);">Most Popular</div>`;
                }

                let featuresHtml = '';
                if (p.features && Array.isArray(p.features)) {
                  featuresHtml = `<ul style="list-style:none;padding:0;margin:0 0 40px;flex:1;">${p.features.map(f => `<li style="display:flex;align-items:flex-start;gap:16px;margin-bottom:20px;font-size:14px;line-height:1.5;${isFeatured ? 'color:#e2e8f0;' : 'color:#334155;'}"><span style="color:#CFA047;font-size:14px;flex-shrink:0;margin-top:2px;">✓</span><span>${f}</span></li>`).join('')}</ul>`;
                }

                const priceText = p.price || 'Custom';
                
                card.innerHTML += `
                  <div style="color:#CFA047;font-size:11px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;margin-bottom:24px;">${p.name}</div>
                  <div style="margin:0 0 8px;display:flex;align-items:baseline;gap:8px;">
                    <span style="font-size:48px;font-weight:800;letter-spacing:-1px;${isFeatured ? 'color:#fff;' : 'color:#0a192f;'}">${priceText}</span>
                    ${p.billing_cycle ? `<span style="font-size:16px;font-weight:500;${isFeatured ? 'color:#94a3b8;' : 'color:#64748b;'}">/ ${p.billing_cycle}</span>` : ''}
                  </div>
                  ${p.description && !isSub ? `<p style="font-size:14px;margin:0 0 40px;line-height:1.6;${isFeatured ? 'color:#cbd5e1;' : 'color:#64748b;'}">${p.description}</p>` : '<div style="height:40px;"></div>'}
                  ${featuresHtml}
                `;
                
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.textContent = p.button_text || (isFeatured ? 'Choose Combo' : 'Get Started');
                btn.style.cssText = `display:block;text-align:center;width:100%;background:${isFeatured ? '#CFA047' : 'transparent'};color:${isFeatured ? '#000' : '#0a192f'};border:${isFeatured ? 'none' : '1px solid #0a192f'};padding:14px 24px;border-radius:30px;font-weight:600;font-size:14px;text-decoration:none;transition:all 0.2s;cursor:pointer;`;
                
                btn.addEventListener('click', (e) => {
                  e.preventDefault();
                  if (p.button_link && p.button_link !== '#' && p.button_link !== '/register') {
                    window.location.href = p.button_link;
                  } else {
                    setModalService(p.name);
                    setIsModalOpen(true);
                  }
                });
                
                card.appendChild(btn);
                return card;
              };

              if (pkgGroup.children && pkgGroup.children.length > 0) {
                // Has sub-packages
                pkgGroup.children.forEach(child => {
                  if (child.is_active) {
                    grid.appendChild(renderPackageCard(child, true));
                  }
                });
              } else {
                // Just render itself
                grid.appendChild(renderPackageCard(pkgGroup, false));
              }

              wrapper.appendChild(grid);
              embed.replaceWith(wrapper);
            } catch (err) {
              console.error('Package embed error', err);
            }
          })();
        });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [page]);

  if (loading) {
    return (
      <>
        <TopBar />
        <Navbar />
        <div style={{ padding: '150px 20px', textAlign: 'center', fontFamily: 'Inter, sans-serif' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !page) {
    return (
      <>
        <TopBar />
        <Navbar />
        <div style={{ padding: '150px 20px', textAlign: 'center', fontFamily: 'Inter, sans-serif' }}>
          <h2 style={{ fontSize: '32px', fontWeight: '700', color: '#0f172a', marginBottom: '16px' }}>404 - Page Not Found</h2>
          <p style={{ color: '#64748b', marginBottom: '24px' }}>The page you are looking for does not exist or has been removed.</p>
          <Link to="/" className="btn btn-primary" style={{ background: '#CFA047', borderColor: '#CFA047' }}>Return Home</Link>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <style>{`
        .dynamic-page-wrap {
          font-family: 'Inter', sans-serif !important;
          color: #334155;
          line-height: 1.8;
          background-color: #ffffff;
        }
        
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .ap-quill-btn {
          display: inline-block;
          padding: 14px 32px;
          background: linear-gradient(135deg, #CFA047 0%, #061830 100%);
          color: #fff !important;
          text-decoration: none;
          border-radius: 50px;
          font-family: 'Inter', sans-serif !important;
          font-weight: 600;
          margin: 16px 0;
          box-shadow: 0 8px 24px rgba(207,160,71,0.3);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .ap-quill-btn:hover {
          transform: translateY(-3px) scale(1.02);
          box-shadow: 0 12px 32px rgba(207,160,71,0.5);
        }
        .dynamic-page-banner {
          position: relative;
          padding: 180px 5% 120px;
          text-align: center;
          color: white;
          background-size: cover !important;
          background-position: center !important;
          background-attachment: fixed !important;
          background-repeat: no-repeat !important;
        }
        .dynamic-page-banner::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(6, 24, 48, 0.7) 0%, rgba(6, 24, 48, 0.95) 100%);
        }
        .dynamic-page-banner-content {
          position: relative;
          z-index: 2;
          width: 100%;
          animation: fadeInDown 1s ease-out;
        }
        .dynamic-page-title {
          font-family: 'Outfit', sans-serif !important;
          font-size: 64px;
          font-weight: 800;
          margin-bottom: 24px;
          line-height: 1.1;
          letter-spacing: -2px;
          text-shadow: 0 4px 12px rgba(0,0,0,0.3);
        }
        .dynamic-page-subtitle {
          font-size: 22px;
          opacity: 0.9;
          font-weight: 300;
          max-width: 1000px;
          margin: 0 auto;
        }
        .dynamic-page-content-container {
          padding: 0 5%;
          position: relative;
          z-index: 3;
          margin-bottom: 80px;
        }
        .dynamic-page-content-area {
          width: 100%;
          padding: 60px 2%;
          font-size: 18px;
          animation: fadeInUp 1s ease-out 0.2s both;
        }
        .dynamic-page-content-area p,
        .dynamic-page-content-area span:not([class^="fa-"]):not([class*=" fa-"]),
        .dynamic-page-content-area a,
        .dynamic-page-content-area li,
        .dynamic-page-content-area div {
          font-family: 'Inter', sans-serif !important;
        }
        .dynamic-page-content-area h1, 
        .dynamic-page-content-area h2, 
        .dynamic-page-content-area h3,
        .dynamic-page-content-area h1 *,
        .dynamic-page-content-area h2 *,
        .dynamic-page-content-area h3 * {
          font-family: 'Outfit', sans-serif !important;
        }
        .dynamic-page-content-area h1, 
        .dynamic-page-content-area h2, 
        .dynamic-page-content-area h3 {
          color: #0f172a;
          margin-top: 40px;
          margin-bottom: 24px;
          font-weight: 800;
          letter-spacing: -0.5px;
        }
        .dynamic-page-content-area h1 { font-size: 42px; }
        .dynamic-page-content-area h2 { font-size: 32px; }
        .dynamic-page-content-area h3 { font-size: 26px; }
        
        .dynamic-page-content-area img {
          max-width: 100%;
          border-radius: 16px;
          margin: 32px 0;
          box-shadow: 0 12px 32px rgba(0,0,0,0.1);
        }
        .dynamic-page-content-area ul, .dynamic-page-content-area ol {
          margin-bottom: 32px;
          padding-left: 28px;
        }
        .dynamic-page-content-area li {
          margin-bottom: 12px;
        }
        .dynamic-page-content-area p {
          margin-bottom: 24px;
          font-weight: 400;
          color: #475569;
        }
        
        @media (max-width: 768px) {
          .dynamic-page-banner {
            padding: 140px 5% 80px;
          }
          .dynamic-page-title {
            font-size: 42px;
          }
          .dynamic-page-subtitle {
            font-size: 18px;
          }
          .dynamic-page-content-container {
            padding: 0 4%;
          }
          .dynamic-page-content-area {
            padding: 40px 2%;
          }
        }

        /* ── Quill visual editor output classes ── */
        .dynamic-page-content-area .ql-align-center { text-align: center; }
        .dynamic-page-content-area .ql-align-right { text-align: right; }
        .dynamic-page-content-area .ql-align-justify { text-align: justify; }
        .dynamic-page-content-area .ql-indent-1 { padding-left: 3em; }
        .dynamic-page-content-area .ql-indent-2 { padding-left: 6em; }
        .dynamic-page-content-area .ql-indent-3 { padding-left: 9em; }
        .dynamic-page-content-area .ql-font-inter { font-family: 'Inter', sans-serif !important; }
        .dynamic-page-content-area .ql-font-outfit { font-family: 'Outfit', sans-serif !important; }
        .dynamic-page-content-area .ql-font-roboto { font-family: 'Roboto', sans-serif !important; }
        .dynamic-page-content-area .ql-font-monospace { font-family: monospace !important; }
        .dynamic-page-content-area .ql-size-10px { font-size: 10px; }
        .dynamic-page-content-area .ql-size-12px { font-size: 12px; }
        .dynamic-page-content-area .ql-size-14px { font-size: 14px; }
        .dynamic-page-content-area .ql-size-16px { font-size: 16px; }
        .dynamic-page-content-area .ql-size-18px { font-size: 18px; }
        .dynamic-page-content-area .ql-size-20px { font-size: 20px; }
        .dynamic-page-content-area .ql-size-24px { font-size: 24px; }
        .dynamic-page-content-area .ql-size-28px { font-size: 28px; }
        .dynamic-page-content-area .ql-size-32px { font-size: 32px; }
        .dynamic-page-content-area .ql-size-36px { font-size: 36px; }
        .dynamic-page-content-area .ql-size-48px { font-size: 48px; }
        .dynamic-page-content-area .ql-size-64px { font-size: 64px; }
        .dynamic-page-content-area blockquote { border-left: 4px solid #CFA047; padding-left: 16px; color: #5a6a7e; margin: 1em 0; }
        .dynamic-page-content-area pre.ql-syntax { background: #0f172a; color: #e2e8f0; padding: 16px 20px; border-radius: 8px; font-family: monospace; font-size: 14px; overflow-x: auto; }
        .dynamic-page-content-area table { border-collapse: collapse; width: 100%; margin: 12px 0; }
        .dynamic-page-content-area td, .dynamic-page-content-area th { border: 1px solid #dde3ea; padding: 8px 12px; }
        .dynamic-page-content-area th { background: #f0f4f8; font-weight: 600; }
        .dynamic-page-content-area hr { border: none; border-top: 2px solid #dde3ea; margin: 20px 0; }
      `}</style>
      <TopBar />
      <Navbar />
      
      <div className="dynamic-page-wrap">
        {/* Banner */}
        <div 
          className="dynamic-page-banner"
          style={{
            background: page.banner_image ? `url(${page.banner_image})` : 'url(https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1920&q=80)'
          }}
        >
          <div className="dynamic-page-banner-content">
            <h1 className="dynamic-page-title">
              {page.banner_title || page.title}
            </h1>
            {page.banner_subtitle && (
              <p className="dynamic-page-subtitle">
                {page.banner_subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div className="dynamic-page-content-container">
          <div className="dynamic-page-content-area">
            <div dangerouslySetInnerHTML={{ __html: page.content }}></div>
          </div>
        </div>
      </div>

      <Footer />
      <ConsultationModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        initialService={modalService} 
      />
    </>
  );
};

export default DynamicPage;
